import React, { FC, useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import {
  AttachmentScanResult,
  fetchAttachmentJudgements,
  fetchFileJudgement,
  ScanResult,
  ScanStatus,
} from 'common/core/file_scan';
import getScopedTranslateMethod from 'common/js_utils/getScopedTranslateMethod';
import { getAttachmentFileId, ViewAttachment } from 'common/types/view';
import { AttachmentLink } from 'common/components/MetadataTable/AttachmentLink';
import { BASE_SCAN_CHECK_INCREMENT } from './constants';

export type AttachmentList = Array<ViewAttachment & { blobId?: string }>;
export interface AttachmentTableProps {
  /** List of attachments to display */
  attachments: AttachmentList;

  /** ID of the view */
  viewId: string;
  /** Whether or not to attempt to re-fetched unscanned results
   * There are pre-2021 files that will always be 'unscanned'
   * on the other hand, if we're currently uploading new attachments (on DSMP)
   * then we expect to eventually see a scan completed.
   */
  refetchUnscanned: boolean;
}

// This is double exported (here and as the default) for testing purposes
export const AttachmentTable: FC<AttachmentTableProps> = (props: AttachmentTableProps) => {
  const { attachments, viewId, refetchUnscanned } = props;
  const t = getScopedTranslateMethod('common.metadata');
  const retryLimit = 3;

  const [scanResults, setScanResults] = useState<AttachmentScanResult>({});
  const [fetchingResults, setFetchingResults] = useState(false);

  /**
   * Method to handle cleanup after fetching scan results
   * @param updatedScanResults Value to set to scanResults in state
   */
  const updateStateValues = useCallback((updatedScanResults: AttachmentScanResult) => {
    setScanResults(updatedScanResults);
    setFetchingResults(false);
  }, []);

  /**
   * Async method to fetch scan results for attachments
   * @param currentAttachments The list of attachments
   * @param currentScanResults The current set of scan results
   * @param retryCount Which retry attempt is this performing
   * @returns Promise
   */
  const fetchScanResults = useCallback(
    async (currentAttachments: AttachmentList, currentScanResults: AttachmentScanResult, retryCount = 0) => {
      const updatedScanResults: AttachmentScanResult = Object.assign({}, currentScanResults);
      // This will be set to true if we need to schedule a refetch attempt
      let needToRefetch: boolean = false;
      if (!isEmpty(currentAttachments)) {
        // Fetch scan results for already published attachments if we haven't already
        if (isEmpty(updatedScanResults)) {
          const results = await fetchAttachmentJudgements(viewId);
          Object.assign(updatedScanResults, results.files || {});
          // very old attachments have 'blobId' instead of assetId
          // and are stored in the assets table. They're returned under this key
          Object.assign(updatedScanResults, results.assets || {});
        }

        // Fill in scan results for any unpublished attachments
        // ***IMPORTANT***
        //    This must be a `map` loop instead of a `forEach`.
        //    The `await` syntax won't work correctly in a `forEach` loop
        //    See https://stackoverflow.com/a/37576787 and its comments for more info
        await Promise.all(
          currentAttachments.map(async (attachment) => {
            const attachmentId = getAttachmentFileId(attachment);

            if (
              attachmentId &&
              // We don't have a scan result for it or it is an attempted refetch
              (!updatedScanResults[attachmentId] ||
                updatedScanResults[attachmentId].state === ScanStatus.Loading)
            ) {
              const scanResult = await fetchFileJudgement(viewId, attachmentId);
              let resultState = scanResult.state;

              // If attachment is new and the scan hasn't finished,
              if (refetchUnscanned && !scanResult.scannedAt && scanResult.state === ScanStatus.Unscanned) {
                // Have we hit the retry limit?
                if (retryCount < retryLimit) {
                  // If not, set the state to "Loading" and flag a refetch is needed
                  resultState = ScanStatus.Loading;
                  needToRefetch = true;
                } else {
                  // If so, set the state to "Unknown" so we won't try to refetch again
                  resultState = ScanStatus.Unknown;
                }
              }

              // Add the scan result to our updated collection
              updatedScanResults[attachmentId] = { ...scanResult, state: resultState };
            }
          }),
        ).then(() => {
          // Schedule a refetch attempt?
          if (needToRefetch) {
            setTimeout(
              () => fetchScanResults(currentAttachments, updatedScanResults, retryCount + 1), // Attempt to fetch again
              (retryCount + 1) * BASE_SCAN_CHECK_INCREMENT, // Incrementally increase how long between refetch attempts
            );
          }
          updateStateValues(updatedScanResults);
        });
      } else {
        updateStateValues(updatedScanResults);
      }
    },
    [viewId, refetchUnscanned],
  );

  /**
   * Determine if we need to fetch scan results for attachments.
   * @param currentAttachments The list of attachments
   * @param currentScanResults The current set of scan results
   * @returns Boolean
   */
  const needToFetchResults = useCallback(
    (currentAttachments: AttachmentList, currentScanResults: AttachmentScanResult) => {
      // We only need to fetch results if there are attachments without a result.

      return (
        currentAttachments.filter((attachment) => {
          const attachmentId = getAttachmentFileId(attachment);

          return attachmentId && !currentScanResults[attachmentId];
        }).length > 0
      );
    },
    [],
  );

  // Get scan results for the attachments
  useEffect(() => {
    // Don't attempt to fetch results if we are already fetching results
    if (!fetchingResults) {
      if (needToFetchResults(attachments, scanResults)) {
        setFetchingResults(true);

        // Somewhat counter intuitively, we have to pass in these values.
        // Referencing them directly from within the fetchScanResults
        //  method DOES NOT WORK.
        fetchScanResults(attachments, scanResults, 0);
      }
    }
  }, [fetchScanResults, needToFetchResults, setFetchingResults, attachments]);

  const attachmentRows = attachments.map((attachment, i) => {
    const attachmentId = getAttachmentFileId(attachment);
    const displayName = attachment.name || attachment.filename;
    const href =
      'assetId' in attachment
        ? `/api/views/${viewId}/files/${attachment.assetId}?download=true&filename=${attachment.filename}`
        : `/api/assets/${attachment.blobId}?download=true`;

    // Default to loading unless we have finished fetching actual results
    let scanResult: ScanResult = { state: ScanStatus.Loading, scannedAt: null };
    if (attachmentId && !isEmpty(scanResults)) {
      scanResult = scanResults[attachmentId] || { state: ScanStatus.Unknown, scannedAt: null };
    }
    return (
      <tr key={i}>
        <td className="attachment">
          <AttachmentLink displayName={displayName} downloadUrl={href} scanResult={scanResult} />
        </td>
      </tr>
    );
  });

  return (
    <div>
      <h3 className="metadata-table-title">{t('attachments')}</h3>
      <table className="table table-condensed table-borderless table-discrete table-striped">
        <tbody>{attachmentRows}</tbody>
      </table>
    </div>
  );
};

export default AttachmentTable;
